import { Platform } from 'react-native';
import { io, Socket } from 'socket.io-client';
import { API_BASE_URL } from '../config/api';
import logger from '../utils/logger';

// استخراج رابط WebSocket من رابط API
// API: https://api.iot-edge.io/api/v1 -> WS: https://api.iot-edge.io
const getWsUrl = () => {
  const baseUrl = API_BASE_URL.replace('/api/v1', '');
  // استخدم نفس دومين API للWebSocket
  return baseUrl;
};

const WS_URL = getWsUrl();

class WebSocketService {
  private socket: Socket | null = null;
  private listeners: Map<string, Set<(data: any) => void>> = new Map();
  private orgId: string | null = null;
  private token: string | null = null;
  private isConnecting = false;

  connect(orgId: string, token: string) {
    // إذا كان الاتصال قائماً بنفس المنظمة والتوكن، لا داعي لإعادة الاتصال
    if (this.socket?.connected && this.orgId === orgId && this.token === token) {
      logger.log('🔌 WebSocket already connected');
      return;
    }

    // تجنب التشغيل المزدوج (إذا كان يحاول الاتصال بالفعل)
    if (this.isConnecting && this.orgId === orgId && this.token === token) {
      logger.log('🔌 WebSocket connection already in progress');
      return;
    }

    this.orgId = orgId;
    this.token = token;
    this.isConnecting = true;

    // إغلاق الاتصال القديم فقط (بدون مسح المستمعين)
    this._closeSocket();

    logger.log('🔌 Connecting to WebSocket:', WS_URL);
    logger.log('🔑 Auth token present:', !!token);
    logger.log('🏢 OrgId:', orgId);

    // إعدادات محسنة: reconnection محدودة لتوفير البطارية
    const socketOptions = {
      auth: { token },
      transports: (Platform.OS as any) === 'web' ? ['polling'] : ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 15, // ← أقصى عدد محاولات (≈ 3 دقائق) لتوفير البطارية
      reconnectionDelay: 2000,
      reconnectionDelayMax: 30000,
      timeout: 20000,
      forceNew: true,
    };

    this.socket = io(WS_URL, socketOptions);

    this.socket.on('connect', () => {
      logger.log('✅ WebSocket connected successfully!');
      this.isConnecting = false;

      // الانضمام لغرفة المنظمة عند كل اتصال (أو إعادة اتصال)
      this.socket?.emit('join', { orgId: this.orgId });
      logger.log('📨 Sent join request for orgId:', this.orgId);

      // إخطار المستمعين بالاتصال الناجح
      this.notifyListeners('connect', {});
    });

    this.socket.on('disconnect', (reason) => {
      logger.warn('🔌 WebSocket disconnected. Reason:', reason);
      // لا نمسح المستمعين - سيستمر socket.io بمحاولة إعادة الاتصال تلقائياً
      // إخطار المستمعين بانقطاع الاتصال
      this.notifyListeners('disconnect', { reason });
    });

    this.socket.on('connect_error', (error) => {
      if (Platform.OS === 'web') {
        logger.log('⚠️ WebSocket connection failed on web (expected)');
        return;
      }
      logger.warn('⚠️ WebSocket connection error (will retry):', error.message);
      // لا نوقف الاتصال يدوياً - نترك socket.io يتولى إعادة المحاولة
    });

    // إخطار المستمعين عند استنفاد جميع محاولات إعادة الاتصال
    this.socket.io.on('reconnect_failed', () => {
      logger.error('❌ WebSocket exhausted all reconnection attempts');
      this.isConnecting = false;
      this.notifyListeners('reconnect-failed', {});
    });

    this.socket.on('error', (error) => {
      if (Platform.OS === 'web') {
        return;
      }
      logger.error('❌ WebSocket error:', error);
    });

    // استقبال بيانات التيليمتري
    this.socket.on('telemetry', (data) => {
      logger.log('📊 Telemetry received via WebSocket:', data.deviceId);
      this.notifyListeners('telemetry', data);
    });

    // استقبال الإنذارات
    this.socket.on('alarm', (data) => {
      logger.log('🚨 Alarm received via WebSocket');
      this.notifyListeners('alarm', data);
    });

    // استقبال تحديثات حالة الأجهزة
    this.socket.on('device-status', (data) => {
      logger.log('📱 Device status update via WebSocket:', data);
      this.notifyListeners('device-status', data);
    });

    // استقبال تحديثات بيانات الأجهزة
    this.socket.on('device-update', (data) => {
      logger.log('🔄 Device update received via WebSocket:', data.id);
      this.notifyListeners('device-update', data);
    });
  }

  /**
   * إغلاق الـ socket الحالي فقط (بدون مسح المستمعين)
   * تُستخدم داخلياً عند إعادة الاتصال بتوكن جديد
   */
  private _closeSocket() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
    this.isConnecting = false;
  }

  /**
   * إغلاق الاتصال عند تسجيل الخروج - يمسح المستمعين أيضاً
   * يجب استدعاؤها فقط عند تسجيل الخروج الكامل
   */
  disconnect() {
    this._closeSocket();
    this.listeners.clear(); // مسح المستمعين فقط عند الخروج الكامل
    this.orgId = null;
    this.token = null;
    this.isConnecting = false;
  }

  /**
   * إعادة الاتصال بتوكن جديد مع الحفاظ على المستمعين المسجّلين
   * تُستخدم بعد تجديد التوكن
   */
  reconnectWithToken(token: string) {
    if (!this.orgId) return;
    logger.log('🔄 Reconnecting WebSocket with fresh token');
    this.connect(this.orgId, token);
  }

  subscribe(event: string, callback: (data: any) => void) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)?.add(callback);

    // إرجاع دالة لإلغاء الاشتراك
    return () => {
      this.listeners.get(event)?.delete(callback);
    };
  }

  private notifyListeners(event: string, data: any) {
    this.listeners.get(event)?.forEach((callback) => {
      try {
        callback(data);
      } catch (error) {
        logger.error('Error in WebSocket listener:', error);
      }
    });
  }

  isConnected(): boolean {
    return this.socket?.connected ?? false;
  }
}

export const websocketService = new WebSocketService();
export default websocketService;
